<script lang="ts">
  import { onDestroy } from "svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { mdiDrag } from "@mdi/js"
  import { resolveStatusColor } from "@kontextwerk/web-sdk/core/display/status"
  import { RICH_TOOLTIP_CLASS } from "../../../../../../../../../../_shared/_logic/rich-tooltips"
  import { buildEventTooltip } from "./_logic/eventPresentation"
  import type { BoardEvent } from "../../../../../../../_logic/calendar-types"
  import AppointmentEventCardContent from "./_modules/appointment-event-card-content"
  import BlockerEventCardContent from "./_modules/blocker-event-card-content"
  import FallbackEventCardContent from "./_modules/fallback-event-card-content"
  import { simplebarMeta } from "./simplebar-meta"

  type ResizeDirection = "start" | "end"
  type ScrollAxes = "vertical" | "both"
  type BoardPropertyRef = NonNullable<BoardEvent["property"]>

  interface Props {
    item: BoardEvent
    minEventHeight?: number
    formatAddress?: (property?: BoardPropertyRef) => string
    employeeLabel?: string
    calendarSyncEnabled?: boolean
    onOpen?: (event: ScheduleEvent) => void
    allowInteractions?: boolean
    allowResizeInteractions?: boolean
    readOnly?: boolean
    showEventTooltips?: boolean
    showAppointmentTagTooltip?: boolean
    scrollAxes?: ScrollAxes
    overridePosition?: { top?: number; height?: number } | null
    useRouteInsets?: boolean
    isActive?: boolean
    ghosted?: boolean
    preview?: boolean
    isPast?: boolean
    onDragStart?: (event: BoardEvent, pointer: PointerEvent) => void
    onResizeStart?: (event: BoardEvent, direction: ResizeDirection, pointer: PointerEvent) => void
  }

  let {
    item,
    minEventHeight = 52,
    formatAddress = () => "",
    employeeLabel = "",
    calendarSyncEnabled = false,
    onOpen = () => {},
    allowInteractions = false,
    allowResizeInteractions = false,
    readOnly = false,
    showEventTooltips = true,
    showAppointmentTagTooltip = true,
    scrollAxes = "vertical",
    overridePosition = null,
    useRouteInsets = true,
    isActive = false,
    ghosted = false,
    preview = false,
    isPast = false,
    onDragStart = null,
    onResizeStart = null,
  }: Props = $props()

  const dragStartThreshold = 6
  const resizeEdgeSize = 16
  let pendingDrag: { pointerId: number; startX: number; startY: number } | null = null
  let suppressNextClick = false
  let suppressClickTimeout: number | null = null
  let resizeEdge = $state<ResizeDirection | null>(null)

  const isScrollbarTarget = (event: Event) => {
    const target = event.target

    return (
      target instanceof Element && Boolean(target.closest(".simplebar-track, .simplebar-scrollbar"))
    )
  }

  const blockNextClick = () => {
    if (suppressClickTimeout) {
      window.clearTimeout(suppressClickTimeout)
    }

    suppressNextClick = true
    suppressClickTimeout = window.setTimeout(() => {
      suppressNextClick = false
      suppressClickTimeout = null
    }, 1200)
  }

  function clearPendingDrag() {
    pendingDrag = null
    window.removeEventListener("pointermove", handleCardPointerMove)
    window.removeEventListener("pointerup", handleCardPointerUp)
    window.removeEventListener("pointercancel", handleCardPointerCancel)
  }

  function handleCardPointerMove(event: PointerEvent) {
    if (!pendingDrag || event.pointerId !== pendingDrag.pointerId) return

    const distance = Math.hypot(
      event.clientX - pendingDrag.startX,
      event.clientY - pendingDrag.startY,
    )

    if (distance < dragStartThreshold) return

    clearPendingDrag()
    blockNextClick()
    onDragStart?.(item, event)
  }

  function handleCardPointerUp(event: PointerEvent) {
    if (!pendingDrag || event.pointerId !== pendingDrag.pointerId) return

    clearPendingDrag()
  }

  function handleCardPointerCancel(event: PointerEvent) {
    if (!pendingDrag || event.pointerId !== pendingDrag.pointerId) return

    clearPendingDrag()
  }

  const resolveResizeEdge = (event: MouseEvent | PointerEvent): ResizeDirection | null => {
    if (!allowResizeInteractions || !onResizeStart) return null

    const target = event.currentTarget

    if (!(target instanceof HTMLElement)) return null

    const rect = target.getBoundingClientRect()
    const y = event.clientY - rect.top
    const edgeSize = Math.min(resizeEdgeSize, rect.height / 4)

    if (y <= edgeSize) return "start"

    if (rect.height - y <= edgeSize) return "end"

    return null
  }

  const handleCardMouseMove = (event: MouseEvent) => {
    resizeEdge = resolveResizeEdge(event)
  }

  const clearResizeEdge = () => {
    resizeEdge = null
  }

  const handleCardPointerDown = (event: PointerEvent) => {
    if (isScrollbarTarget(event)) {
      event.stopPropagation()

      return
    }

    if ((!allowInteractions && !allowResizeInteractions) || (!onDragStart && !onResizeStart)) return

    if (event.button !== 0) return

    if (event.defaultPrevented) return

    const edge = resolveResizeEdge(event)

    if (edge) {
      event.preventDefault()
      event.stopPropagation()
      clearPendingDrag()
      blockNextClick()
      onResizeStart?.(item, edge, event)

      return
    }

    if (!allowInteractions || !onDragStart) return

    clearPendingDrag()
    pendingDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    }

    window.addEventListener("pointermove", handleCardPointerMove)
    window.addEventListener("pointerup", handleCardPointerUp)
    window.addEventListener("pointercancel", handleCardPointerCancel)
  }

  onDestroy(() => {
    clearPendingDrag()

    if (suppressClickTimeout) {
      window.clearTimeout(suppressClickTimeout)
    }
  })

  const routeInsetTop = $derived(useRouteInsets ? (item.routeInsetTopPx ?? 0) : 0)
  const routeInsetBottom = $derived(useRouteInsets ? (item.routeInsetBottomPx ?? 0) : 0)
  const top = $derived((overridePosition?.top ?? item.top ?? 0) + routeInsetTop)
  const height = $derived(
    Math.max(
      1,
      (overridePosition?.height ?? item.height ?? minEventHeight) -
        routeInsetTop -
        routeInsetBottom,
    ),
  )
  const compact = $derived(height < 72)
  const singleLine = $derived(height < 36)
  const isAppointment = $derived(item?.event?.event_type === "APPOINTMENT")
  const borderColor = $derived(
    isAppointment && item.statusDefinition
      ? resolveStatusColor(item.statusDefinition.color)
      : item.color,
  )
  const laneStyle = $derived(
    (item.laneCount ?? 1) > 1
      ? `left:calc(${item.laneLeftPct ?? 0}% + 0.35rem);width:calc(${item.laneWidthPct ?? 100}% - 0.7rem);right:auto;`
      : "",
  )
  const tooltipContent = $derived(
    buildEventTooltip({
      item,
      employeeLabel,
      formatAddress,
      includeAppointmentCommunication: showAppointmentTagTooltip,
    }),
  )
</script>

<div
  role="button"
  aria-disabled={readOnly}
  tabindex={readOnly ? -1 : 0}
  data-calendar-event-id={item.event.id}
  class={`event-card ${allowInteractions || allowResizeInteractions ? "interactive" : ""} ${readOnly ? "readonly" : ""} ${isActive ? "active" : ""} ${ghosted ? "ghosted" : ""} ${preview ? "preview" : ""} ${isPast ? "past" : ""} ${item.dimmed ? "dimmed" : ""} ${scrollAxes === "both" ? "scroll-both" : ""} ${(item.laneCount ?? 1) > 1 ? "parallel-lane" : ""}`}
  class:compact
  class:has-drag-handle={allowInteractions}
  class:single-line={singleLine}
  class:resize-edge={Boolean(resizeEdge)}
  class:resize-edge-start={resizeEdge === "start"}
  class:resize-edge-end={resizeEdge === "end"}
  style={`top:${top}px;height:${height}px;border-left-color:${borderColor};${laneStyle}`}
  use:tooltip={showEventTooltips && tooltipContent && !resizeEdge
    ? { content: tooltipContent, className: RICH_TOOLTIP_CLASS }
    : undefined}
  onmousemove={handleCardMouseMove}
  onmouseleave={clearResizeEdge}
  onpointerdown={handleCardPointerDown}
  onclick={(event) => {
    if (suppressNextClick) {
      suppressNextClick = false

      if (suppressClickTimeout) {
        window.clearTimeout(suppressClickTimeout)
        suppressClickTimeout = null
      }

      event.preventDefault()
      event.stopPropagation()

      return
    }

    if (isScrollbarTarget(event)) {
      event.preventDefault()
      event.stopPropagation()

      return
    }

    if (readOnly) return

    onOpen(item.event)
  }}
  onkeydown={(event) => {
    if (readOnly) return

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onOpen(item.event)
    }
  }}
  title={readOnly ? undefined : "Termin öffnen"}
>
  {#if allowResizeInteractions}
    <span class="resize-handle top" aria-hidden="true"></span>
  {/if}
  {#if allowInteractions}
    <button
      type="button"
      class="drag-handle"
      aria-label="Termin verschieben"
      onpointerdown={(event) => {
        event.preventDefault()
        event.stopPropagation()
        clearResizeEdge()
        blockNextClick()
        onDragStart?.(item, event)
      }}
      onclick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      <Icon path={mdiDrag} size="0.78rem" />
    </button>
  {/if}
  {#if allowResizeInteractions}
    <span class="resize-handle bottom" aria-hidden="true"></span>
  {/if}
  {#if compact}
    <div class="compact-content">
      <span class="compact-address">{item.addressLabel || item.title}</span>
      {#if !singleLine && item.addressLabel && item.title}
        <span class="compact-title">{item.title}</span>
      {/if}
    </div>
  {:else}
    <div
      class="event-meta-scroll calendar-event-simplebar"
      data-scroll-axes={scrollAxes}
      data-simplebar
      data-simplebar-auto-hide="false"
      use:simplebarMeta
    >
      <div class="event-meta">
        {#if isAppointment}
          <AppointmentEventCardContent
            {item}
            {employeeLabel}
            {showEventTooltips}
            {showAppointmentTagTooltip}
            allowHorizontalOverflow={scrollAxes === "both"}
          />
        {:else if item.event.event_type}
          <BlockerEventCardContent
            {item}
            {employeeLabel}
            {calendarSyncEnabled}
            {showEventTooltips}
          />
        {:else}
          <FallbackEventCardContent {item} {formatAddress} {calendarSyncEnabled} />
        {/if}
      </div>
    </div>
  {/if}
</div>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .event-card.compact,
  .event-card.compact.scroll-both {
    padding: 2px 5px;
    box-shadow: 0 2px 5px #0001;
    overflow: hidden;
  }
  .event-card.compact.has-drag-handle {
    padding-left: 24px;
  }
  .compact-content {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    line-height: 15px;
    min-width: 0;
  }
  .compact-address,
  .compact-title {
    color: var(--cal-text, #2f4858);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .compact-address {
    font-weight: 700;
  }
  .single-line .compact-content {
    flex-direction: row;
    gap: 5px;
    line-height: 14px;
  }
  .event-card.compact.single-line {
    padding-top: 1px;
    padding-bottom: 1px;
  }
  .event-card.compact .drag-handle {
    top: 1px;
    left: 3px;
    width: 16px;
    height: 16px;
    box-shadow: none;
  }
  .event-card.compact .resize-handle {
    height: 4px;
  }
  .event-card {
    position: absolute;
    z-index: 2;
    box-sizing: border-box;
    left: 0.6rem;
    right: 0.6rem;
    border: 1px solid var(--cal-border, rgba(0, 0, 0, 0.06));
    border-left: 4px solid var(--kontext-accent);
    border-radius: 8px;
    padding: 0.55rem 0.7rem;
    background: var(--cal-bg, #ffffff);
    color: var(--cal-text, #2f4858);
    text-align: left;
    cursor: pointer;
    box-shadow: 0 12px 32px rgba(47, 72, 88, 0.18);
    overflow: visible;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease,
      opacity 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 16px 36px rgba(0, 0, 0, 0.32);
    }

    &.ghosted {
      opacity: 0.55;
    }

    &.active {
      z-index: 3;
      opacity: 0.9;
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.34);
    }

    &.preview {
      z-index: 4;
      opacity: 0.92;
      pointer-events: none;
      outline: 2px solid color-mix(in srgb, var(--cal-accent, #741e20) 70%, transparent);
      outline-offset: 2px;
      box-shadow: 0 18px 36px rgba(47, 72, 88, 0.24);
    }

    &.interactive {
      user-select: none;
      touch-action: none;
      cursor: grab;
    }

    &.interactive:active {
      cursor: grabbing;
    }

    &.readonly {
      cursor: default;
    }

    &.readonly:hover {
      transform: none;
      box-shadow: 0 12px 32px rgba(47, 72, 88, 0.18);
    }

    &.resize-edge,
    &.resize-edge:active {
      cursor: ns-resize;
    }

    &:focus-visible {
      outline: 2px solid var(--cal-accent, #741e20);
      outline-offset: 2px;
    }

    &.past {
      opacity: 0.6;
      cursor: default;
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.14);
    }

    &.past:hover {
      transform: none;
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.14);
    }

    &.dimmed,
    &.dimmed.past {
      opacity: 0.5;
    }

    &.scroll-both {
      left: 0;
      right: 0;
      padding: 0.46rem 0.48rem 0.78rem;
    }

    .event-meta-scroll {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
      max-height: 100%;
      min-height: 0;
      min-width: 0;
      box-sizing: border-box;
      overflow: visible;
    }

    .event-meta-scroll :global(.simplebar-wrapper),
    .event-meta-scroll :global(.simplebar-mask),
    .event-meta-scroll :global(.simplebar-offset),
    .event-meta-scroll :global(.simplebar-content-wrapper) {
      width: 100%;
      max-width: 100%;
      height: 100%;
      max-height: 100%;
      min-height: 0;
      min-width: 0;
      overflow-x: hidden;
    }

    .event-meta-scroll :global(.simplebar-content-wrapper) {
      overflow-x: hidden !important;
      overflow-y: auto !important;
      scroll-behavior: auto !important;
    }

    .event-meta-scroll :global(.simplebar-content) {
      min-height: 100%;
      min-width: 0;
      box-sizing: border-box;
      padding: 0 !important;
      display: block;
    }

    .event-meta-scroll :global(.simplebar-track.simplebar-horizontal) {
      display: none;
    }

    &.scroll-both .event-meta-scroll {
      flex: 1 1 auto;
      overflow: auto;
    }

    &.scroll-both .event-meta-scroll :global(.simplebar-wrapper),
    &.scroll-both .event-meta-scroll :global(.simplebar-mask),
    &.scroll-both .event-meta-scroll :global(.simplebar-offset),
    &.scroll-both .event-meta-scroll :global(.simplebar-content-wrapper) {
      width: 100%;
      height: 100%;
    }

    &.scroll-both .event-meta-scroll :global(.simplebar-content-wrapper) {
      overflow-x: auto !important;
      overflow-y: auto !important;
    }

    &.scroll-both .event-meta-scroll :global(.simplebar-content) {
      min-width: 100%;
      width: max-content;
    }

    &.scroll-both .event-meta-scroll :global(.simplebar-track.simplebar-horizontal) {
      display: block !important;
      visibility: visible !important;
      height: 8px !important;
      right: 0 !important;
      bottom: 0 !important;
      left: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      opacity: 1 !important;
      background: linear-gradient(
        to bottom,
        transparent 0,
        transparent 3px,
        var(--cal-scrollbar-track, rgba(13, 12, 12, 0.42)) 3px,
        var(--cal-scrollbar-track, rgba(13, 12, 12, 0.42)) 5px,
        transparent 5px
      ) !important;
      pointer-events: auto;
      cursor: pointer;
      z-index: 5;
    }

    &.scroll-both
      .event-meta-scroll
      :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar) {
      display: block !important;
      visibility: visible !important;
      height: 8px !important;
      min-width: 18px;
      opacity: 1 !important;
      pointer-events: auto;
      cursor: grab;
    }

    &.scroll-both
      .event-meta-scroll
      :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar::before) {
      background-color: var(--cal-scrollbar-thumb, #0d0c0c) !important;
      opacity: 1 !important;
      top: 1px !important;
      right: 0 !important;
      bottom: 1px !important;
      left: 0 !important;
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      border-radius: 0;
      transition-delay: 0s;
      transition-duration: 0ms;
    }

    &.scroll-both
      .event-meta-scroll
      :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar:active) {
      cursor: grabbing;
    }

    .event-meta-scroll :global(.simplebar-track.simplebar-vertical) {
      display: none !important;
      visibility: hidden !important;
      width: 8px !important;
      top: 0 !important;
      right: auto !important;
      bottom: 0 !important;
      left: calc(-0.48rem - 2px) !important;
      margin: 0 !important;
      overflow: visible !important;
      opacity: 0 !important;
      background: linear-gradient(
        to right,
        transparent 0,
        transparent 3px,
        var(--cal-scrollbar-track, rgba(13, 12, 12, 0.42)) 3px,
        var(--cal-scrollbar-track, rgba(13, 12, 12, 0.42)) 5px,
        transparent 5px
      ) !important;
      pointer-events: auto;
      cursor: pointer;
      z-index: 5;
    }

    .event-meta-scroll:global(.simplebar-scrollable-y)
      :global(.simplebar-track.simplebar-vertical) {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .event-meta-scroll :global(.simplebar-track.simplebar-vertical .simplebar-scrollbar) {
      display: none !important;
      visibility: hidden !important;
      width: 8px !important;
      min-height: 18px;
      opacity: 0 !important;
      pointer-events: auto;
      cursor: grab;
    }

    .event-meta-scroll:global(.simplebar-scrollable-y)
      :global(.simplebar-track.simplebar-vertical .simplebar-scrollbar) {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .event-meta-scroll :global(.simplebar-track.simplebar-vertical .simplebar-scrollbar::before) {
      background-color: var(--cal-scrollbar-thumb, #0d0c0c) !important;
      opacity: 1 !important;
      top: 0 !important;
      right: 1px !important;
      bottom: 0 !important;
      left: 1px !important;
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      border-radius: 0;
      transition-delay: 0s;
      transition-duration: 0ms;
    }

    .event-meta-scroll :global(.simplebar-track.simplebar-vertical .simplebar-scrollbar:active) {
      cursor: grabbing;
    }

    .event-meta-scroll
      :global(.simplebar-track.simplebar-vertical .simplebar-scrollbar.simplebar-visible::before) {
      opacity: 1 !important;
    }

    .event-meta {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: 0.35rem;
      min-height: 100%;
      min-width: 0;
      width: 100%;
      overflow: visible;
      box-sizing: border-box;

      :global(.event-card.interactive) & {
        padding-left: 1.8rem;
      }
    }

    &.scroll-both .event-meta {
      width: max-content;
      min-width: 100%;
    }
  }

  .drag-handle {
    position: absolute;
    top: 0.3rem;
    left: 0.3rem;
    width: 24px;
    height: 24px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--cal-text, #2f4858);
    border: 1px solid color-mix(in srgb, var(--cal-bg, #ffffff) 72%, transparent);
    color: var(--cal-bg, #ffffff);
    box-shadow: 0 4px 12px rgba(47, 72, 88, 0.24);
    cursor: grab;
    touch-action: none;
    appearance: none;
    padding: 0;
    opacity: 0.1;
    transition:
      opacity 0.12s ease,
      background 0.12s ease,
      box-shadow 0.12s ease,
      transform 0.12s ease;
    z-index: 2;

    &:active {
      cursor: grabbing;
      transform: scale(0.96);
    }

    &:focus-visible {
      outline: 2px solid var(--cal-accent, #741e20);
      outline-offset: 2px;
      opacity: 1;
    }
  }

  .resize-handle {
    position: absolute;
    left: 0;
    right: 0;
    height: 20px;
    border: 0;
    border-radius: 0;
    cursor: ns-resize;
    background: transparent;
    appearance: none;
    padding: 0;
    opacity: 1;
    pointer-events: none;
    touch-action: none;
    z-index: 4;

    &::after {
      content: "";
      position: absolute;
      left: 0.8rem;
      right: 0.8rem;
      height: 4px;
      border-radius: 999px;
      background: color-mix(in srgb, var(--cal-text, #2f4858) 18%, transparent);
      opacity: 0;
      transition: opacity 0.12s ease;
    }

    &.top {
      top: -10px;

      &::after {
        top: 8px;
      }
    }

    &.bottom {
      bottom: -10px;

      &::after {
        bottom: 8px;
      }
    }
  }

  .event-card.interactive:hover .drag-handle {
    background: var(--cal-accent, #741e20);
    box-shadow: 0 6px 16px rgba(47, 72, 88, 0.32);
    opacity: 1;
  }

  .event-card.interactive:hover .resize-handle::after,
  .event-card.resize-edge-start .resize-handle.top::after,
  .event-card.resize-edge-end .resize-handle.bottom::after {
    opacity: 1;
  }
</style>
